import React,{Fragment,useEffect,useState} from "react";
import {
  Button,Card,CardBody,Col,Container,Form,FormGroup,Input,Label,
  Row,Spinner,Table
} from "reactstrap";
import {Breadcrumbs} from "../../AbstractElements";
import {useTranslation} from "react-i18next";
import {titleCase} from "../../utils/case";
import {
  deleteReport,generateReport,getReportOptions,getReports
} from "../../api/report.api";
import "./report.css";

const TYPES={
  reservations_list:"Reservations List",
  reservation_pdf:"Reservation PDF",
  revenue:"Revenue",
  event:"Event Report",
  guest_registration:"Guest Registration Report"
};

const initial={
  startDate:"",endDate:"",
  checkInStartDate:"",checkInEndDate:"",
  registrationStartDate:"",registrationEndDate:"",
  lotId:"",addOnId:"",packageId:"",location:"",status:"",exportType:"excel"
};

export default function Report(){
  const {t}=useTranslation();
  const [type,setType]=useState("reservations_list");
  const [filters,setFilters]=useState(initial);
  const [bookingRef,setBookingRef]=useState("");
  const [eventLotId,setEventLotId]=useState("");
  const [options,setOptions]=useState({lots:[],addOns:[],packages:[],locations:[],events:[]});
  const [reports,setReports]=useState([]);
  const [loading,setLoading]=useState(false);
  const [message,setMessage]=useState("");
  const [reportName,setReportName]=useState("");
  const [showDeletedItems,setShowDeletedItems]=useState(false);

  const load=async()=>{
    try{
      const [o,r]=await Promise.all([getReportOptions(),getReports()]);
      setOptions(o);
      setReports(Object.values(r?.data||{}).flat());
    }catch(err){
      setMessage(err?.response?.data?.message||err.message);
    }
  };

  useEffect(()=>{load();},[]);

  const update=(key,value)=>setFilters(p=>({...p,[key]:value}));

  const submit=async(e)=>{
    e.preventDefault();
    try{
      setLoading(true);setMessage("");
      let configuration={
        startDate:filters.startDate||undefined,
        endDate:filters.endDate||undefined,
        lotId:filters.lotId?Number(filters.lotId):undefined,
        addOnId:filters.addOnId?Number(filters.addOnId):undefined,
        packageId:filters.packageId?Number(filters.packageId):undefined,
        location:filters.location||undefined,
        status:filters.status||undefined,
        exportType:filters.exportType
      };

      if(type==="event")configuration={
        lotId:Number(eventLotId),
        startDate:filters.startDate||undefined,
        endDate:filters.endDate||undefined,
        status:filters.status||undefined,
        exportType:filters.exportType
      };
      if(type==="reservation_pdf")configuration={bookingRef:bookingRef.trim()};
      if(type==="guest_registration")configuration={
        checkInStartDate:filters.checkInStartDate||undefined,
        checkInEndDate:filters.checkInEndDate||undefined,
        registrationStartDate:filters.registrationStartDate||undefined,
        registrationEndDate:filters.registrationEndDate||undefined,
        exportType:filters.exportType
      };

      await generateReport({
        reportName:reportName.trim(),
        type,
        configuration
      });

      setMessage("Report queued successfully.");
      await load();
    }catch(err){
      setMessage(err?.response?.data?.message||err.message);
    }finally{setLoading(false);}
  };

  const remove=async(id)=>{
    if(!window.confirm("Delete this report?"))return;
    try{await deleteReport(id);await load();}
    catch(err){setMessage(err?.response?.data?.message||err.message);}
  };

  return <Fragment>
    <Breadcrumbs
      parent={titleCase(t("reports"))}
      title={titleCase(t("reports"))}
      mainTitle={titleCase(t("reports"))}
    />
    <Container fluid>
      <Row>
        <Col xl="4">
          <Card className="report-card"><CardBody>
            <h5>Generate Report</h5>
            <p className="report-help">Select report type and filters.</p>

            <Form onSubmit={submit}>
              <FormGroup>
                <Label>Report Type</Label>
                <Input type="select" value={type}
                  onChange={e=>{setType(e.target.value);setMessage("");}}>
                  {Object.entries(TYPES).map(([v,l])=><option value={v} key={v}>{l}</option>)}
                </Input>
              </FormGroup>

              <FormGroup>
                <Label>Report File Name</Label>
                <Input
                  type="text"
                  value={reportName}
                  onChange={e=>setReportName(e.target.value)}
                  placeholder="Enter report file name"
                  required
                />
                <small className="form-text text-muted">
                  A timestamp will automatically be added to the file name.
                </small>
              </FormGroup>

              {type==="reservation_pdf"&&<FormGroup>
                <Label>Booking Ref</Label>
                <Input type="text" required value={bookingRef}
                  onChange={e=>setBookingRef(e.target.value)}/>
              </FormGroup>}

              {type==="event"&&<>
                <FormGroup>
                  <Label>Event</Label>
                  <Input type="select" required value={eventLotId}
                    onChange={e=>setEventLotId(e.target.value)}>
                    <option value="">Select event</option>
                    {options.events.map(x=><option key={x.id} value={x.id}>
                      {x.name}{x.location?` — ${x.location}`:""}{x.deletedAt?" [Deleted]":""}
                    </option>)}
                  </Input>
                </FormGroup>
                <Row>
                  <Col md="6"><FormGroup>
                    <Label>Date From</Label>
                    <Input type="date" value={filters.startDate}
                      onChange={e=>update("startDate",e.target.value)}/>
                  </FormGroup></Col>
                  <Col md="6"><FormGroup>
                    <Label>Date To</Label>
                    <Input type="date" value={filters.endDate}
                      onChange={e=>update("endDate",e.target.value)}/>
                  </FormGroup></Col>
                </Row>
              </>}

              {type==="guest_registration"&&<>
                <Row>
                  <Col md="6"><FormGroup>
                    <Label>Check In Date From</Label>
                    <Input type="date" value={filters.checkInStartDate||""}
                      onChange={e=>update("checkInStartDate",e.target.value)}/>
                  </FormGroup></Col>
                  <Col md="6"><FormGroup>
                    <Label>Check In Date To</Label>
                    <Input type="date" value={filters.checkInEndDate||""}
                      onChange={e=>update("checkInEndDate",e.target.value)}/>
                  </FormGroup></Col>
                </Row>
                <Row>
                  <Col md="6"><FormGroup>
                    <Label>Registration Date From</Label>
                    <Input type="date" value={filters.registrationStartDate||""}
                      onChange={e=>update("registrationStartDate",e.target.value)}/>
                  </FormGroup></Col>
                  <Col md="6"><FormGroup>
                    <Label>Registration Date To</Label>
                    <Input type="date" value={filters.registrationEndDate||""}
                      onChange={e=>update("registrationEndDate",e.target.value)}/>
                  </FormGroup></Col>
                </Row>
              </>}

              {type!=="reservation_pdf"&&<FormGroup>
                <Label>Status</Label>
                <Input type="select" value={filters.status}
                  onChange={e=>update("status",e.target.value)}>
                  <option value="">All statuses</option>
                  <option value="pending">Pending</option>
                  <option value="unpaid">Unpaid</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="cancelled">Cancelled</option>
                </Input>
              </FormGroup>}

              {(type==="reservations_list"||type==="revenue")&&<>
                <Row>
                  <Col md="6"><FormGroup>
                    <Label>Date From</Label>
                    <Input type="date" value={filters.startDate}
                      onChange={e=>update("startDate",e.target.value)}/>
                  </FormGroup></Col>
                  <Col md="6"><FormGroup>
                    <Label>Date To</Label>
                    <Input type="date" value={filters.endDate}
                      onChange={e=>update("endDate",e.target.value)}/>
                  </FormGroup></Col>
                </Row>

                <FormGroup>
                  <Label>Lot</Label>
                  <Input type="select" value={filters.lotId}
                    onChange={e=>update("lotId",e.target.value)}>
                    <option value="">All lots</option>
                    {options.lots
                      .filter(x=>!x.isEvent && (showDeletedItems || !x.deletedAt))
                      .map(x=><option key={x.id} value={x.id}>
                        {x.name}{x.location?` — ${x.location}`:""}{x.deletedAt?" [Deleted]":""}
                      </option>)}
                  </Input>
                </FormGroup>

                <FormGroup>
                  <Label>Add-on</Label>
                  <Input type="select" value={filters.addOnId}
                    onChange={e=>update("addOnId",e.target.value)}>
                    <option value="">All add-ons</option>
                    {options.addOns
                      .filter(x=>showDeletedItems || !x.deletedAt)
                      .map(x=><option key={x.id} value={x.id}>
                        {x.name}{x.deletedAt?" [Deleted]":""}
                      </option>)}
                  </Input>
                </FormGroup>

                <FormGroup>
                  <Label>Package</Label>
                  <Input type="select" value={filters.packageId}
                    onChange={e=>update("packageId",e.target.value)}>
                    <option value="">All packages</option>
                    {options.packages
                      .filter(x=>showDeletedItems || !x.deletedAt)
                      .map(x=><option key={x.id} value={x.id}>
                        {x.name}{x.deletedAt?" [Deleted]":""}
                      </option>)}
                  </Input>
                </FormGroup>

                <FormGroup className="mb-3">
                  <div className="checkbox checkbox-primary">
                    <Input
                      id="showDeletedItems"
                      type="checkbox"
                      checked={showDeletedItems}
                      onChange={e=>setShowDeletedItems(e.target.checked)}
                    />
                    <Label for="showDeletedItems">Show deleted items</Label>
                  </div>
                </FormGroup>

                <FormGroup>
                  <Label>Location</Label>
                  <Input type="select" value={filters.location}
                    onChange={e=>update("location",e.target.value)}>
                    <option value="">All locations</option>
                    {options.locations.map(x=><option key={x} value={x}>{x}</option>)}
                  </Input>
                </FormGroup>
              </>}

              {type!=="reservation_pdf"&&<FormGroup>
                <Label>Export Format</Label>
                <Input type="select" value={filters.exportType}
                  onChange={e=>update("exportType",e.target.value)}>
                  <option value="excel">Excel</option>
                  <option value="pdf">PDF</option>
                </Input>
              </FormGroup>}

              {message&&<div className="report-message">{message}</div>}

              <div className="d-flex gap-2">
                <Button color="primary" type="submit" disabled={loading}>
                  {loading?<><Spinner size="sm"/> Generating...</>:"Generate Report"}
                </Button>
                <Button
                  type="button"
                  color="light"
                  onClick={()=>{
                    setFilters(initial);
                    setShowDeletedItems(false);
                    setBookingRef("");
                    setEventLotId("");
                    setReportName("");
                    setShowDeletedItems(false);
                  }}
                >
                  Reset
                </Button>
              </div>
            </Form>
          </CardBody></Card>
        </Col>

        <Col xl="8">
          <Card className="report-card"><CardBody>
            <h5>Generated Reports</h5>
            <p className="report-help">Reports generated by the worker.</p>
            <div className="table-responsive">
              <Table hover>
                <thead><tr>
                  <th>Report</th><th>Type</th><th>Status</th><th>Created</th><th>Action</th>
                </tr></thead>
                <tbody>
                  {!reports.length?<tr><td colSpan="5" className="text-center text-muted">
                    No reports generated yet.
                  </td></tr>:reports.map(r=><tr key={r.id}>
                    <td><strong>{r.reportName}</strong>
                      {r.error&&<div className="report-error">{r.error}</div>}
                    </td>
                    <td>{TYPES[r.type]||r.type}</td>
                    <td><span className={`report-status report-status-${r.status}`}>{r.status}</span></td>
                    <td>{new Date(r.createdAt).toLocaleString("en-MY")}</td>
                    <td className="d-flex gap-2">
                      {r.documentPath&&<>
                        <a
                          className="btn btn-sm btn-outline-secondary"
                          href={`${process.env.REACT_APP_FILES_URL}/${r.documentPath}`}
                          target="_blank" rel="noreferrer">View</a>
                        <a
                          className="btn btn-sm btn-outline-primary"
                          href={`${process.env.REACT_APP_FILES_URL}/${r.documentPath}`}
                          target="_blank" rel="noreferrer" download>Download</a>
                      </>}
                      <Button color="outline-danger" size="sm" onClick={()=>remove(r.id)}>Delete</Button>
                    </td>
                  </tr>)}
                </tbody>
              </Table>
            </div>
          </CardBody></Card>
        </Col>
      </Row>
    </Container>
  </Fragment>;
}
